import Axios from 'axios'
import { DocumentInboxItem } from 'src/viewModels/workflow/DocumentInboxItem'
import { InboxSummary } from 'src/viewModels/workflow/InboxSummary'
import { IncomingDocumentSummary } from 'src/viewModels/IncomingDocumentSummary'
import { IProcessAction, ProcessAction } from 'src/viewModels/workflow/ProcessAction'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { UserDropdownOption } from 'src/viewModels/UserDropdownOption'
import { pagedList } from 'src/viewModels/pagedList'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import { WorkflowInboxItemHeader } from 'src/viewModels/workflow/InboxItemHeader'
import { ClaimInboxItem } from 'src/viewModels/workflow/ClaimInboxItem'
import { StagedDocumentDTO } from 'src/viewModels/StagedDocumentDTO'
import { CMS1500Dto, ICMS1500Dto } from 'src/viewModels/claim/Cms1500Dto'
import { ClaimValidationSummary } from 'src/viewModels/workflow/ClaimValidationSummary'
import { QuoteInboxItem } from 'src/viewModels/workflow/QuoteInboxItem'
import { InboxItemType } from 'src/utils/InboxItemType'
import { BillOutInboxItem } from 'src/viewModels/workflow/BillOutInboxItem'
import { BilloutDetailSummary } from 'src/viewModels/workflow/BilloutDetailSummary'
import { Combined1500GeneralSummary } from 'src/viewModels/workflow/Combined1500GeneralSummary'
import { CaseAppointmentStatusOption } from '../viewModels/workflow/CaseAppointmentStatusOption'
import { Combined1500ComponentDetail } from '../viewModels/workflow/Combined1500ComponentDetail'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { BulkActionResult } from 'src/viewModels/workflow/BulkActionResult'
import { ICaseSummaryResult } from 'src/viewModels/workflow/CaseSummaryResult'
import { MedicalPatientDTO } from 'src/viewModels/workflow/MedicalPatientDTO'
import { PatientInvoiceDetailDTO } from 'src/viewModels/workflow/PatientInvoiceDetailDTO'
import { InvoicePaymentSummary } from 'src/viewModels/workflow/InvoicePaymentSummary'
import { VerifyCheckRequest } from 'src/viewModels/workflow/VerifyCheckRequest'
import { ProcessInvoicePaymentCheckWithCheck } from 'src/viewModels/ProcessInvoicePaymentCheckWithCheck'
import { FixedInboxSearchRequest } from 'src/viewModels/workflow/FixedInboxFilterRequest'
import { CreateClaimValidationResult } from 'src/viewModels/workflow/CreateClaimValidationResult'
import { ClaimEventSummary } from 'src/viewModels/workflow/ClaimEventSummary'
import { InvoiceInboxItem } from '../viewModels/workflow/InvoiceInboxItem'
import { BulkInvoiceEmailResult } from 'src/viewModels/workflow/BulkInvoiceEmailResult'
import { BulkInvoiceEmailRequest } from 'src/viewModels/workflow/BulkInvoiceEmailRequest'
import { IGlobalInvoicePaymentSummary } from 'src/viewModels/InvoicePaymentSummaryResult'
import { Combined1500ClaimFieldSummary } from 'src/viewModels/quotes/Combined1500ClaimFieldSummary'
import { InboxTypeSummary } from 'src/viewModels/workflow/InboxTypeSummary'
import { WorkflowInboxUserFavorite } from 'src/viewModels/workflow/WorkflowInboxUserFavorite'

const base_url = process.env.REACT_APP_WORKFLOW_BASE_URL || 'http://localhost:5002'

export async function getAllIncomingDocuments(
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/document/incoming-documents`,
    data
  )

  return response.data
}

export async function getAllInboxItems(
  data: FixedInboxSearchRequest,
  inboxId: string,
  sortColumnQuery: string
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/inbox/${inboxId}/inbox-items`,
    data,
    {
      params: {
        sort: sortColumnQuery,
      },
    }
  )

  return response.data
}

export async function getAllBulkActions(
  inboxId: string
): Promise<Array<InboxActionSummary>> {
  const response = await Axios.get<Array<InboxActionSummary>>(
    `${base_url}/api/inbox/${inboxId}/actions`,
    {
      params: {
        isBulkProcessingOnly: true,
      },
    }
  )

  return response.data
}

export async function getInboxesByUser(
  type?: InboxItemType
): Promise<Array<InboxSummary>> {
  const response = await Axios.get<Array<InboxSummary>>(`${base_url}/api/inbox`, {
    params: {
      inboxType: type,
    },
  })

  return response.data
}

export async function processToWorkflow(
  data: IncomingDocumentSummary
): Promise<DocumentInboxItem> {
  const response = await Axios.post<DocumentInboxItem>(
    `${base_url}/api/document/workflow`,
    data
  )

  return response.data
}

export async function getDocumentInboxItem(
  documentId: string,
  inboxId: string
): Promise<DocumentInboxItem> {
  const response = await Axios.get<DocumentInboxItem>(
    `${base_url}/api/document/${documentId}/inbox/${inboxId}`
  )

  return response.data
}

export async function getDefaultDocumentInboxItem(
  documentId: string
): Promise<DocumentInboxItem> {
  const response = await Axios.get<DocumentInboxItem>(
    `${base_url}/api/document/${documentId}/default-inbox`
  )

  return response.data
}

export async function processAction(
  data: IProcessAction,
  actionId: string,
  itemId: string
): Promise<WorkflowInboxItemHeader> {
  const response = await Axios.post<WorkflowInboxItemHeader>(
    `${base_url}/api/inbox-item/${itemId}/actions/${actionId}`,
    data
  )

  return response.data
}

export async function validateCreateClaim(
  eventData: ClaimEventSummary
): Promise<CreateClaimValidationResult> {
  const response = await Axios.post<CreateClaimValidationResult>(
    `${base_url}/api/claim/validate-create-claim`,
    eventData
  )

  return response.data
}

export async function processBulkAction(
  data: IProcessAction,
  actionId: string
): Promise<Array<BulkActionResult>> {
  const response = await Axios.post<Array<BulkActionResult>>(
    `${base_url}/api/action/${actionId}/bulk-process`,
    data
  )

  return response.data
}

export async function processDynamicAction(
  data: ProcessAction,
  inboxId: string,
  actionId: string,
  itemId: string
): Promise<WorkflowInboxItemHeader> {
  const response = await Axios.post<WorkflowInboxItemHeader>(
    `${base_url}/api/inbox/${inboxId}/actions/${actionId}/process`,
    data,
    {
      params: {
        itemId: itemId,
      },
    }
  )

  return response.data
}

export async function GetUserOptions(
  inboxId: string,
  actionId: string
): Promise<Array<UserDropdownOption>> {
  const response = await Axios.get<Array<UserDropdownOption>>(
    `${base_url}/api/inbox/${inboxId}/actions/${actionId}/assign-users`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  return response.data
}

export async function GetPatientOptions(
  patientInfo: string
): Promise<Array<DropdownOption>> {
  const response = await Axios.get<Array<DropdownOption>>(
    `${base_url}/api/lookup/patients`,
    {
      params: {
        filter: patientInfo,
      },
    }
  )

  return response.data
}

export async function getClaimInboxItem(
  claimId: string,
  inboxId: string
): Promise<ClaimInboxItem> {
  const response = await Axios.get<ClaimInboxItem>(
    `${base_url}/api/claim/${claimId}/inbox/${inboxId}`
  )

  return response.data
}

export async function getDefaultClaimInboxItem(claimId: string): Promise<ClaimInboxItem> {
  const response = await Axios.get<ClaimInboxItem>(
    `${base_url}/api/claim/${claimId}/default-inbox`
  )

  return response.data
}

export async function getStagedDocument(docId: string): Promise<StagedDocumentDTO> {
  const response = await Axios.get<StagedDocumentDTO>(`${base_url}/api/document/${docId}`)

  return response.data
}

export async function getQuoteInboxItem(
  quoteId: string,
  inboxId: string
): Promise<QuoteInboxItem> {
  const response = await Axios.get<QuoteInboxItem>(
    `${base_url}/api/quote/${quoteId}/inbox/${inboxId}`
  )

  return response.data
}

export async function getDefaultQuoteInboxItem(quoteId: string): Promise<QuoteInboxItem> {
  const response = await Axios.get<QuoteInboxItem>(
    `${base_url}/api/quote/${quoteId}/default-inbox`
  )

  return response.data
}

export async function getBilloutInboxItem(
  billoutId: string,
  inboxId: string
): Promise<BillOutInboxItem> {
  const response = await Axios.get<BillOutInboxItem>(
    `${base_url}/api/billout/${billoutId}/inbox/${inboxId}`
  )

  return response.data
}

export async function getDefaultBilloutInboxItem(
  billoutId: string
): Promise<BillOutInboxItem> {
  const response = await Axios.get<BillOutInboxItem>(
    `${base_url}/api/billout/${billoutId}/default-inbox`
  )

  return response.data
}

export async function getBulkEmailDetails(
  bulkInvoiceEmailRequest: BulkInvoiceEmailRequest
): Promise<BulkInvoiceEmailResult[]> {
  const response = await Axios.post<BulkInvoiceEmailResult[]>(
    `${base_url}/api/invoice/bulk-email`,
    bulkInvoiceEmailRequest
  )
  return response.data
}

export async function getInvoiceInboxItem(
  invoiceId: string,
  inboxId: string
): Promise<InvoiceInboxItem> {
  const response = await Axios.get<InvoiceInboxItem>(
    `${base_url}/api/invoice/${invoiceId}/inbox/${inboxId}`
  )

  return response.data
}

export async function getDefaultInvoiceInboxItem(
  invoiceId: string
): Promise<InvoiceInboxItem> {
  const response = await Axios.get<InvoiceInboxItem>(
    `${base_url}/api/invoice/${invoiceId}/default-inbox`
  )

  return response.data
}

export async function getDocumentHistory(
  docId: string,
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/document/${docId}/history`,
    data
  )

  return response.data
}

export async function getCaseHistory(
  caseId: string,
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/case/${caseId}/history`,
    data
  )

  return response.data
}

export async function getClaimHistory(
  claimId: string,
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/claim/${claimId}/history`,
    data
  )

  return response.data
}

export async function getQuoteHistory(
  quoteId: string,
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/quote/${quoteId}/history`,
    data
  )

  return response.data
}

export async function CopyQuoteFromSourceViaWorkflow(
  targetQuoteId: string,
  inboxId: number,
  request: {
    sourceQuoteId: string
    lineItemIds: { id: string; type: string }[]
    assignedClaimIds: string[]
  }
): Promise<void> {
  await Axios.post(
    `${base_url}/api/quote/${targetQuoteId}/inbox/${inboxId}/copy-from-source`,
    request
  )
}

export async function getBilloutHistory(
  billoutId: string,
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/billout/${billoutId}/history`,
    data
  )

  return response.data
}

export async function getInvoiceHistory(
  invoiceId: string,
  data: SearchRequestBase
): Promise<pagedList> {
  const response = await Axios.post<pagedList>(
    `${base_url}/api/invoice/${invoiceId}/history`,
    data
  )

  return response.data
}

export async function GetInvoicePaymentDetailsByInvoiceId(
  invoiceId: string
): Promise<IGlobalInvoicePaymentSummary> {
  const response = await Axios.get<IGlobalInvoicePaymentSummary>(
    `${base_url}/api/invoice/${invoiceId}/invoice-payment-details`
  )
  return response.data
}

export async function completeValidation(
  claimId: string,
  requireContract: boolean,
  data: ICMS1500Dto
): Promise<ClaimValidationSummary> {
  const response = await Axios.post<ClaimValidationSummary>(
    `${base_url}/api/claim/${claimId}/complete-validation`,
    data,
    {
      params: {
        requireContract: requireContract,
      },
    }
  )

  return response.data
}

export async function sendToOcrValidation(
  claimId: string,
  data: ICMS1500Dto
): Promise<ClaimValidationSummary> {
  const response = await Axios.post<ClaimValidationSummary>(
    `${base_url}/api/claim/${claimId}/ocr-validation`,
    data
  )

  return response.data
}

export async function GetBilloutDetailsByClaim(
  claimId: string
): Promise<BilloutDetailSummary> {
  const response = await Axios.get<BilloutDetailSummary>(
    `${base_url}/api/lookup/claim/${claimId}/billout-detail-summary`
  )

  return response.data
}

export async function GetClaimReadyForBilloutInbox(): Promise<number> {
  const response = await Axios.get<number>(
    `${base_url}/api/lookup/claim-ready-for-billout-inbox`
  )

  return response.data
}

export async function GetCombined1500GeneralSummaryAsync(
  quoteId: string
): Promise<Combined1500GeneralSummary> {
  const response = await Axios.get<Combined1500GeneralSummary>(
    `${base_url}/api/quote/${quoteId}/combined-1500-general-summary`
  )

  return response.data
}

export async function GetCombined1500ClaimFieldSummaryAsync(
  claimId: string
): Promise<Combined1500ClaimFieldSummary> {
  const response = await Axios.get<Combined1500ClaimFieldSummary>(
    `${base_url}/api/quote/${claimId}/combined-1500-claim-field-summary`
  )

  return response.data
}

export async function RemoveClaimFromGlobal(
  id: string,
  commentRequest: { comment: string }
): Promise<string> {
  const response = await Axios.post<string>(
    `${base_url}/api/claim/${id}/bundle-remove`,
    commentRequest
  )
  return response.data
}

export async function AddClaimToGlobal(id: string, quoteId: string): Promise<string> {
  const response = await Axios.post<string>(
    `${base_url}/api/claim/${id}/bundle-add`,
    null,
    {
      params: { quoteId },
    }
  )
  return response.data
}

export async function GetCombined1500ComponentDetail(
  quoteId: string,
  providerTypeAbbreviation: string
): Promise<Combined1500ComponentDetail> {
  const response = await Axios.get<Combined1500ComponentDetail>(
    `${base_url}/api/quote/${quoteId}/combined-1500-line-items/${providerTypeAbbreviation}`
  )

  return response.data
}

export async function GetCombined1500GeneralSummary(
  quoteId: string
): Promise<Combined1500GeneralSummary> {
  const response = await Axios.get<Combined1500GeneralSummary>(
    `${base_url}/api/quote/${quoteId}/combined-1500-general`
  )

  return response.data
}

export async function GetCaseInboxItem(caseId: string, inboxId: string): Promise<any> {
  const response = await Axios.get<any>(`${base_url}/api/case/${caseId}/inbox/${inboxId}`)

  return response.data
}

export async function GetDefaultCaseInboxItem(caseId: string): Promise<any> {
  const response = await Axios.get<any>(`${base_url}/api/case/${caseId}/default-inbox`)

  return response.data
}

export async function GetCaseLineItemDetail(
  caseLineItemId: number,
  forApplyPayment: boolean
): Promise<Array<DropdownOption>> {
  const response = await Axios.get<Array<DropdownOption>>(
    `${base_url}/api/Lookup/${caseLineItemId}/case-details`,
    {
      params: { forApplyPayment },
    }
  )

  return response.data
}

export async function fetchCaseStatuses(): Promise<{ value: string; label: string }[]> {
  const response = await fetch(`${base_url}/api/Lookup/case-statuses`, {
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  })
  if (!response.ok) return []
  const data = await response.json()
  return data.map((status: { id: number; name: string }) => ({
    value: String(status.id),
    label: status.name,
  }))
}

export async function createCaseWithPatient(requestBody: ICaseSummaryResult) {
  const response = await Axios.post(`${base_url}/api/case`, requestBody, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export async function getPatientDetailsByCaseId(id: number) {
  const response = await Axios.get<MedicalPatientDTO>(
    `${base_url}/api/Lookup/case-patient/${id}`
  )
  return response.data
}

export async function updatePatientDetails(requestBody: MedicalPatientDTO) {
  const response = await Axios.post(`${base_url}/api/case`, requestBody, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export async function getCaseDetails(id: number) {
  const response = await Axios.get<PatientInvoiceDetailDTO>(
    `${base_url}/api/Lookup/case-detail/${id}`
  )
  return response.data
}

export async function updateCaseDetails(requestBody: PatientInvoiceDetailDTO) {
  const response = await Axios.post(`${base_url}/api/case`, requestBody, {
    headers: {
      'Content-Type': 'application/json',
    },
  })
  return response.data
}

export async function getItemApptStatuses(): Promise<CaseAppointmentStatusOption[]> {
  const response = await Axios.get<CaseAppointmentStatusOption[]>(
    `${base_url}/api/Lookup/case-appt-statuses`
  )
  return response.data
}

export async function GetPaymentDetails(caseId: number): Promise<InvoicePaymentSummary> {
  const response = await Axios.get<InvoicePaymentSummary>(
    `${base_url}/api/Lookup/case-line-item/${caseId}/invoice-payment-details`
  )
  return response.data
}

export async function verifyReceivedCheck(
  verifyCheckRequest: VerifyCheckRequest
): Promise<ProcessInvoicePaymentCheckWithCheck> {
  const response = await Axios.post<ProcessInvoicePaymentCheckWithCheck>(
    `${base_url}/api/Lookup/verify-received-check`,
    verifyCheckRequest
  )
  return response.data
}

export async function saveAppealClaim(cms1500Dto: CMS1500Dto): Promise<any> {
  const response = await Axios.post<string>(
    `${base_url}/api/claim/create-appeal`,
    cms1500Dto
  )
  return response.data
}

export async function getInboxes(
  inboxType?: InboxItemType,
  isFixed?: boolean
): Promise<Array<InboxSummary>> {
  const response = await Axios.get<Array<InboxSummary>>(`${base_url}/api/inbox`, {
    params: {
      inboxType,
      isFixed,
    },
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export async function getInboxTypes(
  inboxType?: InboxItemType,
  isFixed?: boolean
): Promise<Array<InboxTypeSummary>> {
  const response = await Axios.get<Array<InboxTypeSummary>>(
    `${base_url}/api/lookup/inbox-types`,
    {
      params: {
        inboxType,
        isFixed,
      },
      headers: { 'Content-Type': 'application/json' },
    }
  )
  return response.data
}

export async function updateInboxFavorites(requestBody: WorkflowInboxUserFavorite[]) {
  const response = await Axios.post(
    `${base_url}/api/inbox/update-favorites`,
    requestBody,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )
  return response.data
}
